import React from 'react';
import PropTypes from 'prop-types';
import { getDroppedOrSelectedFiles } from 'html5-file-selector';

import Icon from 'components/Icon';
import Paragraph from 'components/Paragraph';

import {
  InputFileMainStyled,
  InputFileStyled,
  InputFileColumn,
  DashedBoxWrapper,
  DashedBox,
} from './InputFile.styled';

const InputFile = props => {
  const {
    children,
    imgNode,
    iconName,
    message,
    removeBorder,
    setOnDragEnter,
    setOnDragLeave,
  } = props;

  const runOnChange = event => {
    getDroppedOrSelectedFiles(event).then(list => {
      if (list.length) props.setOnChange(list);
      return list.map(({ fileObject }) => fileObject);
    });
  };

  if (imgNode) {
    return <InputFileMainStyled>{imgNode}</InputFileMainStyled>;
  }

  return (
    <InputFileMainStyled
      defaultMode={!children}
      disabled={props.disabled}
      removeOpacity={props.removeOpacity}
    >
      <InputFileStyled
        type='file'
        accept={props.accept}
        multiple={props.multiple}
        disabled={props.disabled}
        onDragEnter={event => (setOnDragEnter ? setOnDragEnter(event) : {})}
        onDragLeave={event => (setOnDragLeave ? setOnDragLeave(event) : {})}
        onChange={runOnChange}
      />
      {children ? (
        <>
          {!removeBorder && (
            <DashedBoxWrapper>
              <DashedBox shareFile={props.shareFile} />
            </DashedBoxWrapper>
          )}
          <InputFileColumn>{children}</InputFileColumn>
        </>
      ) : (
        <>
          {!removeBorder && (
            <DashedBoxWrapper>
              <DashedBox default />
            </DashedBoxWrapper>
          )}
          <InputFileColumn>
            {iconName && <Icon name={iconName} size='xlarge' />}
            {message && <Paragraph margin='default 0'>{message}</Paragraph>}
          </InputFileColumn>
        </>
      )}
    </InputFileMainStyled>
  );
};

InputFile.defaultProps = {
  accept: 'image/*', // any image file by default
  multiple: false, // just one file by default
  iconName: 'upload',
  message: 'Drag and drop or click to add an image',
  shareFile: false,
  removeBorder: false,
  removeOpacity: false,
  disabled: false,
};

InputFile.propTypes = {
  /**
   * React children node. If you use children prop you will remove iconName and message props (the default mode). Children prop will create a total custom view center by default | https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file
   */
  children: PropTypes.node,
  /**
   * React node, image preview. If you use imgNode prop you will render just imgNode
   */
  imgNode: PropTypes.node,
  /**
   * The accept attribute value is a string that defines the file types the file input should accept => https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/accept
   */
  accept: PropTypes.string,
  /**
   * A Boolean which, if present, indicates that the user may choose more than one file
   */
  multiple: PropTypes.bool,
  /**
   * SyntheticEvent onDragEnter, output an 'event' as payload
   */
  setOnDragEnter: PropTypes.func,
  /**
   * SyntheticEvent onDragLeave, output an 'event' as payload
   */
  setOnDragLeave: PropTypes.func,
  /**
   * Main output. SyntheticEvent onChange, output a list of files as payload
   */
  setOnChange: PropTypes.func.isRequired,
  /**
   * The icon name prop
   */
  iconName: PropTypes.string,
  /**
   * The message showed for the user
   */
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * If it is on share a file page
   */
  shareFile: PropTypes.bool,
  /**
   * Remove outside dashed border box
   */
  removeBorder: PropTypes.bool,
  /**
   * Remove opacity effect, by default all th component has opacity of 0.6 and on hover 1
   */
  removeOpacity: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default InputFile;
